import { type KeyboardEvent, useEffect, useRef } from 'react';
import cx from 'classnames';

import { type CallbackN, type VoidFunction } from 'src/lib/types';

import styles from './CommentInput.module.scss';

interface CommentInputProps {
  className?: string;
  value: string;
  placeholder: string;
  autoFocus?: boolean;
  dataTestId?: string;
  onChange: CallbackN<[string]>;
  onSubmit: VoidFunction;
  onCancel?: VoidFunction;
}

export const CommentInput = ({
  className,
  value,
  placeholder,
  autoFocus,
  dataTestId,
  onChange,
  onSubmit,
  onCancel,
}: CommentInputProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // A panel is placed only after it renders, so focusing the field must not scroll to it. The caret
  // goes to the end of the text the field opens with.
  useEffect(() => {
    const input = inputRef.current;
    if (autoFocus && input) {
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, [autoFocus]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      onCancel?.();
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <textarea
      ref={inputRef}
      className={cx(styles.root, className)}
      value={value}
      placeholder={placeholder}
      rows={1}
      data-testid={dataTestId}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={handleKeyDown}
    />
  );
};
